import React from "react";
import * as UI from "./ui.jsx";
import { authorizedFetch } from "./boot.js";
import { sydneyWhen } from "./time.js";
import { count as n, errorCode, visiblePoll } from "./stats_shared.js";

// Stats' Inspections tab (spec 2026-09-24 Part 3), mirroring the X-ray views
// tab. The lots list is vendor.inspections.lot_counts@1, counted by the host
// over the vendor's whole log; the lot page is vendor.inspections.for_lot@1,
// grouped here by inspector the way the inspector book folds names. Voided
// visits are listed, struck through, and counted nowhere. Notes are
// vendor-internal and are not shown on Stats.

// Someone may be logging visits as the vendor watches; a minute is soon enough.
const POLL_MS = 60_000;

const breeding = (lot) => [lot?.sire, lot?.dam].filter(Boolean).join(" × ");
// inspection_name_models.fold: one space, no case, no edges.
const fold = (name) => String(name || "").replace(/\s+/g, " ").trim().toLowerCase();
const tidy = (name) => String(name || "").replace(/\s+/g, " ").trim();
// Read together, as X-ray views and vets are: "17 (12 inspectors)" (owner,
// 25 September 2026). Nobody inspected reads a bare 0.
const inspectors = (value) => (typeof value === "number" ? `${value} inspector${value === 1 ? "" : "s"}` : null);

function useRead(path) {
  const [state, setState] = React.useState({ data: null, error: null });
  const [retry, setRetry] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    let requestNumber = 0;
    const controller = new AbortController();
    setState({ data: null, error: null });
    const load = async () => {
      const request = ++requestNumber;
      try {
        const res = await authorizedFetch(path, { signal: controller.signal });
        const body = await res.json().catch(() => ({}));
        if (cancelled || request !== requestNumber) return;
        if (!res.ok) {
          setState((current) => ({ ...current, error: { code: errorCode(body, res.status), status: res.status } }));
          return;
        }
        setState({ data: body, error: null });
      } catch (caught) {
        if (cancelled || request !== requestNumber || caught?.name === "AbortError") return;
        setState((current) => ({ ...current, error: { code: "BROWSER_FETCH_FAILED" } }));
      }
    };
    load();
    const stop = visiblePoll(load, POLL_MS);
    return () => { cancelled = true; controller.abort(); stop(); };
  }, [path, retry]);
  return { ...state, retry: () => setRetry((value) => value + 1) };
}

export function InspectionsTab({ sale, vendor }) {
  const [selected, setSelected] = React.useState(null);
  const query = new URLSearchParams({ sale, vendor });
  const counts = useRead(`/collections/vendor.inspections.lot_counts@1/?${query}`);
  const rows = counts.data && counts.data.sale === sale && counts.data.vendor === vendor
    ? counts.data.lots
    : null;
  const row = selected && rows?.find((item) => item.lot.id === selected);

  return (
    <div className="insp-tab">
      {!rows && !counts.error && <p role="status">Loading inspections…</p>}
      {!rows && counts.error && (
        <div role="alert" className="card" style={{ marginTop: 18 }}>
          <p>
            {counts.error.status === 401 || counts.error.status === 403 || counts.error.status === 404
              ? "Inspection counts are not available for this session."
              : "Inspection counts could not be loaded."}
          </p>
          <button type="button" className="btn" onClick={counts.retry}>Retry</button>
        </div>
      )}
      {rows && row && (
        <InspectionLotPage key={row.lot.id} sale={sale} vendor={vendor} row={row} onBack={() => setSelected(null)}/>
      )}
      {rows && !row && <InspectionLots rows={rows} onOpen={setSelected}/>}
    </div>
  );
}

function InspectionLots({ rows, onOpen }) {
  const { Card } = UI;
  return (
    <Card className="flush insp-lots" style={{ marginTop: 18 }}>
      <div className="xray-lots-head">
        <p className="caption">
          Every visit logged for each lot, with how many inspectors made them. Voided visits
          are not counted, and an inspector is counted once however their name was typed.
        </p>
      </div>
      <table className="table stack-table">
        <thead><tr>
          <th>Lot</th>
          <th>Horse</th>
          <th style={{ textAlign: "right" }}>Inspections</th>
          <th>Last inspected</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan="4" className="stack-full" style={{ textAlign: "center", padding: 36, color: "var(--fg-muted)" }}>
              The catalogue returned no lots.
            </td></tr>
          )}
          {rows.map((row) => (
            <tr key={row.lot.id} className={row.inspections ? undefined : "quiet"}>
              <td data-label="Lot">
                <button type="button" className="lot-detail-link" onClick={() => onOpen(row.lot.id)}
                        aria-label={`Open inspections for lot ${row.lot.number}`}>{row.lot.number}</button>
                {row.lot.withdrawn && <span className="caption"> Withdrawn</span>}
              </td>
              <td data-label="Horse">{breeding(row.lot) || "—"}</td>
              <td data-label="Inspections" style={{ textAlign: "right" }}>
                {n(row.inspections)}{row.inspectors > 0 ? ` (${inspectors(row.inspectors)})` : ""}
              </td>
              <td data-label="Last inspected" className="mono">{sydneyWhen(row.lastInspectedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function InspectionLotPage({ sale, vendor, row, onBack }) {
  const { Card } = UI;
  const panelRef = React.useRef(null);
  const [open, setOpen] = React.useState(() => new Set());
  const query = new URLSearchParams({ sale, vendor, lot_id: row.lot.id });
  const read = useRead(`/collections/vendor.inspections.for_lot@1/?${query}`);
  const result = read.data && read.data.sale === sale && read.data.lot?.id === row.lot.id ? read.data : null;
  const name = breeding(row.lot);

  React.useEffect(() => { panelRef.current?.focus(); }, [row.lot.id]);

  const visits = (result?.inspections || []).slice()
    .sort((a, b) => (a.inspectedAt < b.inspectedAt ? 1 : a.inspectedAt > b.inspectedAt ? -1 : 0));
  const groups = new Map();
  for (const visit of visits) {
    if (visit.voided) continue;
    const key = fold(visit.buyer);
    const group = groups.get(key) || { key, name: tidy(visit.buyer), visits: [], first: null, last: null };
    group.visits.push(visit);
    if (!group.first || visit.inspectedAt < group.first) group.first = visit.inspectedAt;
    if (!group.last || visit.inspectedAt > group.last) group.last = visit.inspectedAt;
    groups.set(key, group);
  }
  const groupRows = [...groups.values()];
  const toggle = (key) => setOpen((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <div className="insp-lot" ref={panelRef} tabIndex="-1" aria-label={`Inspections of lot ${row.lot.number}`}>
      <Card title={`Lot ${row.lot.number}`} subtitle={name || undefined} style={{ marginTop: 18 }}
            right={<button type="button" className="btn insp-back" onClick={onBack}>Back to all lots</button>}>
        <div className="qstrip" style={{ margin: "0 0 18px", display: "flex" }}>
          <Tile label="Inspections" value={n(row.inspections)} sub={inspectors(row.inspectors)}/>
          <Tile label="Last inspected" value={sydneyWhen(row.lastInspectedAt)} small/>
        </div>
        {!result && !read.error && <p role="status">Loading this lot's inspections…</p>}
        {!result && read.error && (
          <div role="alert">
            <p>This lot's inspections could not be loaded.</p>
            <button type="button" className="btn" onClick={read.retry}>Retry</button>
          </div>
        )}
        {result && visits.length === 0 && <p role="status">No one has inspected this lot yet.</p>}
        {result && groupRows.length > 0 && (
          <section className="insp-by-inspector">
            <h3>Inspections by inspector</h3>
            <table className="table stack-table">
              <thead><tr>
                <th>Inspector</th><th style={{ textAlign: "right" }}>Visits</th><th>First seen</th><th>Last seen</th>
              </tr></thead>
              <tbody>
                {groupRows.map((group) => {
                  const expanded = open.has(group.key);
                  return (
                    <React.Fragment key={group.key}>
                      <tr className="group-row">
                        <td data-label="Inspector">
                          <button type="button" className="vet-toggle" aria-expanded={expanded ? "true" : "false"}
                                  onClick={() => toggle(group.key)}>{group.name}</button>
                        </td>
                        <td data-label="Visits" style={{ textAlign: "right" }}>{n(group.visits.length)}</td>
                        <td data-label="First seen" className="mono">{sydneyWhen(group.first)}</td>
                        <td data-label="Last seen" className="mono">{sydneyWhen(group.last)}</td>
                      </tr>
                      {expanded && (
                        <tr className="vet-actions-row">
                          <td colSpan="4" className="stack-full">
                            <div className="vet-actions group-rows">
                              <h4>Visits by {group.name}</h4>
                              <table className="table stack-table">
                                <thead><tr><th>When</th><th>Agent</th><th>Logged by</th></tr></thead>
                                <tbody>
                                  {group.visits.map((visit) => (
                                    <tr key={visit.id}>
                                      <td data-label="When" className="mono">{sydneyWhen(visit.inspectedAt)}</td>
                                      <td data-label="Agent">{visit.agent || "—"}</td>
                                      <td data-label="Logged by">{visit.loggedByName || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}
        {result && visits.length > 0 && (
          <section className="insp-activity">
            <h3>Activity</h3>
            <table className="table stack-table">
              <thead><tr><th>When</th><th>Inspector</th><th>Agent</th><th>Logged by</th></tr></thead>
              <tbody>
                {visits.map((visit) => {
                  const cell = (value) => (visit.voided ? <s>{value}</s> : value);
                  return (
                    <tr key={visit.id} className={visit.voided ? "voided" : undefined}>
                      <td data-label="When" className="mono">{cell(sydneyWhen(visit.inspectedAt))}</td>
                      <td data-label="Inspector">
                        {cell(tidy(visit.buyer))}
                        {visit.voided && <span className="tag"> Voided</span>}
                      </td>
                      <td data-label="Agent">{cell(visit.agent || "—")}</td>
                      <td data-label="Logged by">{cell(visit.loggedByName || "—")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}
      </Card>
    </div>
  );
}

function Tile({ label, value, sub, small }) {
  return (
    <div className="qstat">
      <div className="qstat-label">{label}</div>
      <div className={`num stat-tile-value${small ? " small" : ""}`}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
